import { convertDomainArrayToMap } from '../../../library/Core/SmartFunctions';
import logger from '../../../library/Core/logger';
import { axiosClient } from '../../../library/Service/axiosClient';
import AppSettingsService from '../../../services/app-settings.service';
import DomainDataService from '../../../services/domain-data.service';

const BASE = process.env.REACT_APP_INTERNAL_ADMIN_SERVER || '';
const ADMIN_BASE = process.env.REACT_APP_ADMIN_API_SERVER || '';

class CommitmentService {
    /**
     * Capture page loader.
     * Loads prior commitment rows for this alumni + the full domain map
     * (PATHWAY_GOAL / PATHWAY_LEVEL / PATHWAY_SIGNAL / INSTITUTE_DOMAIN ...).
     * Returns the React-Router loader payload in the
     * `{ formConfig, data, domain, routeInfo, internal }` shape consumed by
     * all other PageBuilder pages (see InstituteService.pageLoader).
     */
    static pageLoader = (params: any) => {
        const state: any = {};
        const { instituteId = '', userId = '' } = params;
        logger.log(`Loading CommitmentCapture for institute=${instituteId} user=${userId}`);

        return Promise.all([
            CommitmentService.getByUser(userId, instituteId),
            CommitmentService.getAlumniProfile(userId, instituteId),
            CommitmentService.getCallLogs(userId, instituteId),
            DomainDataService.get(),
            AppSettingsService.get(),
        ])
            .then((values) => {
                state.formConfig = {};
                state.data = {
                    instituteId,
                    userId,
                    priorSignals: values[0] ?? [],
                    alumniProfile: values[1] ?? {},
                    callLogs: values[2] ?? [],
                };
                state.domain =
                    values[3].data instanceof Map
                        ? values[3].data
                        : convertDomainArrayToMap(values[3].data);
                state.internal = {};
                state.routeInfo = { instituteId, userId, pageName: 'commitment-capture' };
                return Promise.resolve(state);
            })
            .catch(function (err) {
                logger.log(`Error in loading CommitmentCapture data:`);
                console.log(err);
            });
    };

    /**
     * Pulls the minimum the capture-form needs for the Alumni Information card —
     * name, batch, company, phone/email. Uses the existing repo endpoint
     * `${REACT_APP_COMMON_API}/users/get-alumnidetails-by-uuid` (already used by
     * AlumniEngagementGrid / AlumniInteractionGrid / AlumniSelectedConversions).
     * Fails soft (returns {}) so the form still renders if the call fails.
     */
    static async getAlumniProfile(
        userId: string,
        instituteId: number | string
    ): Promise<any> {
        if (!userId) return {};
        try {
            const res = await axiosClient().post(
                `${process.env.REACT_APP_COMMON_API}/users/get-alumnidetails-by-uuid`,
                { alumniId: userId, instituteId }
            );
            const a = res?.data?.data?.[0] ?? {};
            const name = [a.firstName, a.lastName].filter(Boolean).join(' ').trim();
            return {
                aName: name,
                aBatch: a.passOutYear || '',
                // Program name from user_institute_snapshot.program_name —
                // shown in the Alumni Information card (replaced the
                // company display).
                programName: a.programName || a.program_name || '',
                // Raw IDs — kept for click-to-call / future re-introduction
                // of the company column.
                companyId: a.companyId ?? null,
                otherCompany: a.otherCompany ?? '',
                // Raw mobile fields preserved so the click-to-call modal can
                // build the phoneNumber payload (countryCode + mobile) and the
                // input field can render `aPhone` as a display string.
                mobileCountryCode: a.mobileCountryCode || '',
                mobileNumber: a.mobileNumber || '',
                registeredEmailId: a.registeredEmailId || '',
                aPhone:
                    a.mobileNumber
                        ? `${a.mobileCountryCode || ''} ${a.mobileNumber}`.trim()
                        : a.registeredEmailId || '',
                raw: a,
            };
        } catch (e) {
            console.log('getAlumniProfile failed', e);
            return {};
        }
    }

    /**
     * Tracker page loader (institute-wide).
     * The dashboard rollup is fetched per active goal from the component
     * (because the goal tab is user-driven); the loader only hydrates the
     * domain map so PATHWAY_GOAL / PATHWAY_LEVEL / PATHWAY_SIGNAL and
     * INSTITUTE_DOMAIN are available via state.domain.
     */
    static trackerLoader = (params: any) => {
        const state: any = {};
        const { instituteId = '' } = params;
        logger.log(`Loading CommitmentTracker for institute=${instituteId}`);

        return Promise.all([
            DomainDataService.get(),
            AppSettingsService.get(),
        ])
            .then((values) => {
                state.formConfig = {};
                state.data = {
                    instituteId,
                    dashboard: null,
                };
                state.domain =
                    values[0].data instanceof Map
                        ? values[0].data
                        : convertDomainArrayToMap(values[0].data);
                state.internal = {};
                state.routeInfo = { instituteId, pageName: 'commitment-tracker' };
                return Promise.resolve(state);
            })
            .catch(function (err) {
                logger.log(`Error in loading CommitmentTracker data:`);
                console.log(err);
            });
    };

    static async save(payload: any): Promise<any> {
        const res = await axiosClient().post(`${BASE}/commitment/save`, payload);
        return res.data.data;
    }

    /**
     * Lists admin_call_logs rows for (userId, institute) — newest first.
     * Used by the capture form's "Call Session" dropdown so the agent can
     * pick which call session their edits attach to.
     * Reuses the existing commonapi endpoint; the SQL was extended to also
     * return `id` and `purpose_category` / `purpose_subcat` for the dropdown
     * label (other consumers ignore the extra columns).
     */
    static async getCallLogs(
        userId: string,
        instituteId: number | string
    ): Promise<any[]> {
        if (!userId || !instituteId) return [];
        try {
            const res = await axiosClient().post(
                `${process.env.REACT_APP_COMMON_API}/internal-call-log/get-all-call-logs-data`,
                { userId, instituteId }
            );
            return res?.data?.success ? res.data.data ?? [] : [];
        } catch (e) {
            console.log('getCallLogs failed', e);
            return [];
        }
    }

    static async getByCall(adminCallLogId: number | string): Promise<any[]> {
        const res = await axiosClient().get(`${BASE}/commitment/by-call/${adminCallLogId}`);
        return res.data.data;
    }

    static async getByUser(
        userId: string,
        instituteId: number | string,
        goalCode?: string
    ): Promise<any[]> {
        if (!userId || !instituteId) return [];
        const params: Record<string, string | number> = { userId, instituteId };
        if (goalCode) params.goalCode = goalCode;
        const res = await axiosClient().get(`${BASE}/commitment/by-user`, { params });
        return res.data.data;
    }

    /**
     * Dashboard rollup for the tracker.
     *
     * POST body filters:
     *   - instituteId (required) — numeric
     *   - goalCode → restrict to one PATHWAY_GOAL (driven by the active goal tab)
     *   - search   → free-text. Server fuzzy-matches against alumni name
     *                ({first_name last_name}), batch (pass_out_year), and
     *                company (companies_snapshot.name / job.other_company).
     *                Triggered by the tracker's Enter / search-icon click —
     *                not on keystroke.
     *
     * Note: level filter is NOT sent — level cards need full-cohort counts,
     * so the FE applies the level filter client-side.
     */
    static async getDashboard(
        instituteId: number | string | Array<number | string>,
        goalCode?: string,
        filters?: { search?: string | null }
    ): Promise<any> {
        // Accept either a single id (per-institute admin tracker page) or an
        // array of ids (management Commitment Dashboard with multi-campus
        // selector). Backend `getDashboard` accepts both shapes — see
        // CommitmentService.js.
        const body: Record<string, any> = {};
        if (Array.isArray(instituteId)) {
            body.instituteIds = instituteId
                .map((v) => Number(v))
                .filter((v) => Number.isFinite(v));
        } else {
            body.instituteId = instituteId;
        }
        if (goalCode) body.goalCode = goalCode;
        if (filters?.search) body.search = filters.search;
        const res = await axiosClient().post(`${BASE}/commitment/dashboard`, body);
        return res.data.data;
    }

    /**
     * Save institute commitment paths (PATHWAY_GOAL codes opted in by internal admin).
     * Hits admin-master-data-api via REACT_APP_ADMIN_API_SERVER.
     */
    static async saveCommitmentPaths(
        instituteId: number | string,
        commitmentPaths: string[]
    ): Promise<any> {
        const res = await axiosClient().post(
            `${ADMIN_BASE}/institute/savecommitmentPath`,
            { instituteId: Number(instituteId), commitmentPaths }
        );
        return res.data;
    }
}

export default CommitmentService;
